import { Fragment, useMemo, type MouseEvent, type ReactNode } from "react";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Separator } from "@design-system/Separator/Separator";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { Task } from "@common/types/task";
import { trackEvent } from "@lib/analytics";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { getEntityAvatarName } from "@helpers/entityAvatarName";
import { WorkspaceAvatarGroup } from "@components/avatar/WorkspaceAvatarGroup";
import { useEntitiesById } from "@hooks/useEntities";
import { OneClickStatus } from "../OneClickStatus/OneClickStatus";
import { TaskStatusTerminationConfirmationModal } from "../TaskStatusTerminationConfirmationModal";
import { useTaskStatusChange } from "../useTaskStatusChange";
import {
    useTaskClientIconColor,
    useTaskClientName,
} from "../taskClientNamesContext";
import { useTaskPaneState, useTasksContext } from "../tasks.context";
import { PANE_TOGGLE_DROP_ZONE_ID } from "../chatDrag/chatDropZones";
import { useSpringLoadedDropZone } from "../chatDrag/useSpringLoadedDropZone";
import {
    TaskCloseButton,
    TaskPager,
    TaskPaneToggleButton,
    type TaskPagerProps,
} from "./TaskBodyHeaderControls";
import { TaskBodyPeople } from "./TaskBodyPeople";
import styles from "./TaskBodyHeader.module.scss";

interface TaskBodyHeaderProps extends TaskPagerProps {
    selectedTask: Task;
    close: () => void;
    hasTaskInList: boolean;
    hasUnreadMessages: boolean;
    onTogglePane: (event: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Who the task is for, after the title: the entities' stack and names, then
 * `· Client` when known; the client alone when the task has no entities.
 */
const TaskContext = ({ task }: { task: Task }) => {
    const entitiesById = useEntitiesById();
    const entities = useMemo(
        () =>
            task.entityIds?.flatMap((id) => {
                const entity = entitiesById.get(id);
                return entity ? [entity] : [];
            }) ?? [],
        [entitiesById, task.entityIds],
    );
    const clientName = useTaskClientName(task.workspaceId);
    const clientIconColor = useTaskClientIconColor(task.workspaceId);
    const [firstEntity] = entities;

    if (!firstEntity) {
        return clientName ? (
            <WorkspaceAvatarGroup
                workspaces={[
                    {
                        workspace: {
                            id: task.workspaceId,
                            name: clientName,
                            iconColor: clientIconColor,
                        },
                    },
                ]}
                className={styles.entities}
                label={clientName}
            />
        ) : null;
    }

    const firstName = getEntityAvatarName(firstEntity);
    const entitiesLabel =
        entities.length > 1
            ? `${firstName} +${entities.length - 1} more`
            : firstName;

    return (
        <EntityAvatarGroup
            entities={entities}
            max={3}
            className={styles.entities}
            label={
                clientName ? `${entitiesLabel} · ${clientName}` : entitiesLabel
            }
        />
    );
};

/**
 * The open task's top bar: the mark-done circle, title, entities and client
 * on the left; who is on the task, the pager, the pane toggle and close on
 * the right. The pager sits between fixed-width controls so its buttons stay
 * put while paging past tasks with more or fewer people.
 */
export const TaskBodyHeader = ({
    selectedTask,
    close,
    hasTaskInList,
    hasUnreadMessages,
    onTogglePane,
    ...pagerProps
}: TaskBodyHeaderProps) => {
    const { taskDetailPanelCollapsed } = useTaskPaneState();
    const { showTaskDetailPanel } = useTasksContext();
    const setPaneToggleDropZoneRef = useSpringLoadedDropZone({
        id: PANE_TOGGLE_DROP_ZONE_ID,
        onActivate: () => showTaskDetailPanel("chat"),
    });
    const { handleStatusChange, confirmation } =
        useTaskStatusChange(selectedTask);

    const onPaneToggleClick = (event: MouseEvent<HTMLButtonElement>) => {
        void trackEvent(AnalyticsEvent.TASK_DETAIL_PANEL_CLICKED, {
            action: taskDetailPanelCollapsed ? "expand" : "collapse",
            taskId: selectedTask.id,
            taskType: selectedTask.type,
        });
        onTogglePane(event);
    };

    const endGroups: Array<{ key: string; content: ReactNode }> = [
        { key: "people", content: <TaskBodyPeople task={selectedTask} /> },
    ];
    if (hasTaskInList && pagerProps.totalTasks > 1) {
        endGroups.push({
            key: "pager",
            content: <TaskPager {...pagerProps} />,
        });
    }
    endGroups.push({
        key: "actions",
        content: (
            <div className={styles.actions}>
                <TaskPaneToggleButton
                    isPaneCollapsed={taskDetailPanelCollapsed}
                    hasUnreadMessages={hasUnreadMessages}
                    onClick={onPaneToggleClick}
                    ref={setPaneToggleDropZoneRef}
                />
                <TaskCloseButton onClick={close} />
            </div>
        ),
    });

    return (
        <>
            <TaskTopBar
                start={
                    <>
                        <div className={styles.statusAndTitle}>
                            <OneClickStatus
                                value={selectedTask.status}
                                onChange={handleStatusChange}
                            />
                            <BreakableText
                                text={selectedTask.title}
                                className={styles.title}
                                showTooltip
                            />
                        </div>
                        <TaskContext task={selectedTask} />
                    </>
                }
                end={endGroups.map(({ key, content }, index) => (
                    <Fragment key={key}>
                        {index > 0 && (
                            <Separator
                                orientation="vertical"
                                className={styles.separator}
                            />
                        )}
                        {content}
                    </Fragment>
                ))}
            />
            <TaskStatusTerminationConfirmationModal {...confirmation} />
        </>
    );
};

interface TaskTopBarProps {
    start?: ReactNode;
    end: ReactNode;
}

/** The open task's full-width top bar frame, shared with the fallback shown when a task can't be loaded. */
export const TaskTopBar = ({ start, end }: TaskTopBarProps) => (
    <div className={styles.topBar}>
        <div className={styles.start}>{start}</div>
        <div className={styles.end}>{end}</div>
    </div>
);
